import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Coins, ShieldCheck, AlertTriangle, BugPlay, Network } from 'lucide-react';
import { SOURCE_LABELS, SOURCE_BADGE_VARIANTS, type ErrorSource } from '@/lib/errorCosts';
import type { UserDetail } from '../types';

const SOURCE_FALLBACK = (s: string) => SOURCE_LABELS[s as ErrorSource] ?? s;
const VARIANT_FALLBACK = (s: string) => SOURCE_BADGE_VARIANTS[s as ErrorSource] ?? 'outline';

export function ErrorsPanel({ detail }: { detail: UserDetail }) {
  const refunds = detail.refunds ?? [];
  const totalRefunded = detail.stats?.refunds_credits_total ?? 0;

  const last24h = refunds.filter(
    (r: any) => new Date(r.created_at).getTime() > Date.now() - 24 * 60 * 60 * 1000
  );
  const last1h = refunds.filter(
    (r: any) => new Date(r.created_at).getTime() > Date.now() - 60 * 60 * 1000
  );
  const flagged = last24h.length >= 10;

  const status = detail.subscription?.auto_refund_paused_by_admin
    ? { label: 'Paused by admin', tone: 'destructive' as const }
    : detail.subscription?.auto_refund_review_required
      ? { label: 'Review required', tone: 'warning' as const }
      : flagged
        ? { label: 'Flagged', tone: 'warning' as const }
        : { label: 'Healthy', tone: 'success' as const };

  return (
    <div className="space-y-5">
      {/* Auto-refund summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={<Coins className="h-3.5 w-3.5" />}
          label="Total refunds"
          value={refunds.length}
          sub={`${totalRefunded} credits`}
        />
        <MetricCard
          label="Last 24h"
          value={`${last24h.length} / 15`}
          sub="cap 15/day"
          tone={last24h.length >= 10 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Last 1h"
          value={`${last1h.length} / 5`}
          sub="cap 5/hour"
          tone={last1h.length >= 4 ? 'warning' : 'default'}
        />
        <MetricCard
          icon={
            status.tone === 'success' ? (
              <ShieldCheck className="h-3.5 w-3.5" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5" />
            )
          }
          label="Status"
          value={status.label}
          tone={status.tone}
          isText
        />
      </div>

      {/* Auto-refund history */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <Coins className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            Auto-refund history{' '}
            <span className="text-muted-foreground font-normal">({refunds.length})</span>
          </h4>
        </div>
        {refunds.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center">No refunds issued.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <Th>Date</Th>
                  <Th>Source</Th>
                  <Th align="right">Cost</Th>
                  <Th align="right">Bonus</Th>
                  <Th align="right">Refunded</Th>
                  <Th>By</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((r: any) => (
                  <tr key={r.id} className="border-t border-border/60">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap text-xs">
                      {format(new Date(r.created_at), 'dd.MM.yy HH:mm')}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={VARIANT_FALLBACK(r.source) as any} className="text-xs">
                        {SOURCE_FALLBACK(r.source)}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono tabular-nums">
                      {r.original_cost}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono tabular-nums text-success">
                      +{r.bonus}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium font-mono tabular-nums">
                      {r.credits_refunded}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <Badge variant={r.admin_id ? 'default' : 'secondary'} className="text-xs">
                        {r.admin_id ? 'admin' : 'auto'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[260px]">
                      {r.reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reported errors */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <BugPlay className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            Reported errors{' '}
            <span className="text-muted-foreground font-normal">
              ({detail.error_reports.length})
            </span>
          </h4>
        </div>
        {detail.error_reports.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center">No reports.</p>
        ) : (
          <div className="p-4 space-y-2">
            {detail.error_reports.map((r: any) => {
              const ctx = r.error_context ?? {};
              const autoRefunded = ctx.auto_refunded === true || r.status === 'refunded';
              const refundAmount = ctx.refund_amount ?? ctx.amount_refunded;
              return (
                <div
                  key={r.id}
                  className="border border-border/60 rounded-lg p-3 bg-background/40"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                    <div className="font-medium text-sm">{r.error_title}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {autoRefunded && (
                        <Badge className="text-xs gap-1 bg-success/15 text-success border border-success/30 hover:bg-success/20">
                          <Coins className="h-3 w-3" />
                          Refunded{refundAmount ? ` ${refundAmount}` : ''}
                        </Badge>
                      )}
                      <Badge
                        variant={
                          r.status === 'new'
                            ? 'destructive'
                            : r.status === 'resolved' || r.status === 'refunded'
                              ? 'default'
                              : 'secondary'
                        }
                      >
                        {r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(r.created_at), 'dd.MM.yy HH:mm')}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono whitespace-pre-wrap break-words bg-muted/30 rounded p-2">
                    {r.error_message}
                  </div>
                  {ctx.transaction_id && (
                    <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">
                      tx: {ctx.transaction_id}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Microsoft Graph errors */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <Network className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            Microsoft Graph API errors{' '}
            <span className="text-muted-foreground font-normal">
              ({detail.graph_errors.length})
            </span>
          </h4>
        </div>
        {detail.graph_errors.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center">No Graph errors.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <Th>Date</Th>
                  <Th>Method</Th>
                  <Th>Endpoint</Th>
                  <Th align="right">Status</Th>
                  <Th>Error</Th>
                </tr>
              </thead>
              <tbody>
                {detail.graph_errors.map((e: any) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap text-xs">
                      {format(new Date(e.created_at), 'dd.MM HH:mm')}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="text-xs">
                        {e.method}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono truncate max-w-[200px]">
                      {e.endpoint}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Badge variant={e.status >= 500 ? 'destructive' : 'secondary'}>
                        {e.status ?? '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[300px]">
                      {e.error_code ? `[${e.error_code}] ` : ''}
                      {e.error_message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`text-${align} px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground`}
    >
      {children}
    </th>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sub,
  tone = 'default',
  isText = false,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: 'default' | 'warning' | 'success' | 'destructive';
  isText?: boolean;
}) {
  const toneClass = {
    default: 'text-foreground',
    warning: 'text-warning',
    success: 'text-success',
    destructive: 'text-destructive',
  }[tone];

  return (
    <div className="rounded-xl border border-border/60 bg-card p-3.5">
      <div
        className={`text-xs flex items-center gap-1 font-semibold uppercase tracking-wide ${
          tone === 'default' ? 'text-muted-foreground' : toneClass
        }`}
      >
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 ${isText ? 'text-sm font-medium' : 'text-lg font-semibold tabular-nums'} ${toneClass}`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
